import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useAuth } from "../contexts/AuthContext";
import LoginForm from "../components/auth/LoginForm";
import CreateAdminForm from "../components/auth/CreateAdminForm";
import OnboardingForm from "../components/auth/OnboardingForm";
import { LoginElement } from "soolui";

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 500px;
`;

const Logo = styled.div`
  color: white;
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 40px;
  text-align: center;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const TabContainer = styled.div`
  display: flex;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 4px;
  margin-bottom: 32px;
  backdrop-filter: blur(10px);
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  color: ${(props) => (props.$active ? "#333" : "rgba(255, 255, 255, 0.8)")};
  background: ${(props) => (props.$active ? "white" : "transparent")};

  &:hover {
    color: ${(props) => (props.$active ? "#333" : "white")};
    background: ${(props) =>
      props.$active ? "white" : "rgba(255, 255, 255, 0.1)"};
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: white;
  font-size: 1.1rem;
`;

const ErrorMessage = styled.div`
  background: rgba(220, 38, 38, 0.1);
  border: 1px solid rgba(220, 38, 38, 0.3);
  color: white;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;
  text-align: center;
  backdrop-filter: blur(10px);
`;

type AuthMode = "login" | "register" | "create-admin" | "onboarding";

const AuthPage: React.FC = () => {
  const { user, checkFirstUser, login } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [authMode, setAuthMode] = useState<AuthMode>("login");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const initializePage = async () => {
      try {
        setLoading(true);
        setError("");

        // Check if user is already authenticated
        if (user) {
          navigate("/dashboard");
          return;
        }

        // Check for onboarding token in URL
        const onboardingToken = searchParams.get("token");
        if (onboardingToken) {
          setAuthMode("onboarding");
          setLoading(false);
          return;
        }

        // Check if this is the first user
        const firstUserResult = await checkFirstUser();
        if (firstUserResult.success && firstUserResult.isFirstUser) {
          setAuthMode("create-admin");
        } else {
          setAuthMode("login");
        }
      } catch (err) {
        console.error("Error initializing auth page:", err);
        setError(
          "Impossibile inizializzare l'autenticazione. Ricarica la pagina."
        );
        setAuthMode("login");
      } finally {
        setLoading(false);
      }
    };

    initializePage();
  }, [user, searchParams, navigate, checkFirstUser]);

  const handleModeChange = (mode: AuthMode) => {
    setAuthMode(mode);
    setError("");
  };

  const handleLogin = async (email: string, password: string) => {
    try {
        setLoading(true)
        setError("")
        const result = await login(email, password)
        if (result.success) {
            navigate('/dashboard')
        } else {
            setError(result.message || "Credenziali non valide")
            setLoading(false)
        }
    } catch(e) {
        console.error(e)
        setError("Si è verificato un errore durante l'accesso")
        setLoading(false)
    }
  };

  // Use LoginElement for login mode
  if (authMode === 'login' && !loading) {
     return (
       <LoginElement 
         title="Benvenuto in LeadMe"
         subtitle="Accedi per visualizzare la dashboard"
         emailLabel="Indirizzo Email"
         passwordLabel="Password"
         buttonText="Accedi"
         footerText={`© ${new Date().getFullYear()} Soolutions. Tutti i diritti riservati.`}
         onSubmit={handleLogin}
         error={error}
         isLoading={loading}
       />
     )
  }

  const renderAuthForm = () => {
    switch (authMode) {
      case "login":
        return <LoginForm />;
      case "create-admin":
        return (
          <CreateAdminForm onSwitchToLogin={() => handleModeChange("login")} />
        );
      case "onboarding":
        return <OnboardingForm />;
      default:
        return <LoginForm />;
    }
  };

  const renderTabs = () => {
    // Don't show tabs for onboarding or create-admin modes
    if (authMode === "onboarding" || authMode === "create-admin") {
      return null;
    }

    return (
      <TabContainer>
        <Tab
          $active={authMode === "login"}
          onClick={() => handleModeChange("login")}
        >
          Accedi
        </Tab>
      </TabContainer>
    );
  };

  if (loading) {
    return (
      <PageContainer>
        <ContentWrapper>
          <Logo>Lead Me</Logo>
          <LoadingSpinner>Inizializzazione...</LoadingSpinner>
        </ContentWrapper>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ContentWrapper>
        <Logo>Lead Me</Logo>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        {renderTabs()}
        {renderAuthForm()}
      </ContentWrapper>
    </PageContainer>
  );
};

export default AuthPage;
